import {
  activeQuestionGroupChild,
  isQuestionGroupContainerRaw,
} from "../questions/questionGroup/core";
import { AUTO_RESPONSE_MSG_CONTENT } from "../util/constants";
import type { Question, QuestionReference } from "../util/types";
import {
  DEFAULT_SURVEY_RETRY_POLICY,
  requestWithRecovery,
  type SurveyRetryPolicy,
} from "./retry";
import type {
  StoredValueEntry,
  SurveyTranscriptEntry,
  TranscriptProjection,
} from "./transcript";
import type { SurveyAnswer, SurveyCommand, SurveyTransport } from "./transport";
import { formatCopy, type SurveyCopy } from "../theme/copy";
import { defaultThemeV2 } from "../theme/defaultThemeV2";

export type SurveyAttempt = Readonly<{
  id: number;
  question: Question;
  startedAtMs: number;
  availableAtMs: number;
}>;

export type SurveySessionPhase =
  | "idle"
  | "loading"
  | "active"
  | "submitting"
  | "recovering"
  | "complete"
  | "error"
  | "disposed";

export type SurveySessionSnapshot = Readonly<{
  phase: SurveySessionPhase;
  attempt: SurveyAttempt | null;
  pending: SurveyCommand["kind"] | null;
  canSubmit: boolean;
  canUndo: boolean;
  transcript: readonly SurveyTranscriptEntry[];
  storedValues: readonly StoredValueEntry[];
  error: Error | null;
}>;

type PendingCommand = {
  command: SurveyCommand;
  controller: AbortController;
  deletedValue?: StoredValueEntry;
};

export type PreparedSurveyAnswer = Readonly<{
  answer: SurveyAnswer;
  projection: TranscriptProjection;
}>;

type GroupSubmission = {
  groupId: string;
  resolve: (question: Question) => PreparedSurveyAnswer | null;
  lastChildId: string;
};

type CommentSubmission = {
  body: string;
  sourceId: string;
  questionReference: QuestionReference | null;
};

const MAX_TIMEOUT_MS = 2_147_483_647;

const questionDelayMs = (question: Question): number => {
  if (question.question.type === "complete") return 0;
  const raw = isQuestionGroupContainerRaw(question.question)
    ? activeQuestionGroupChild(question.question)?.question
    : question.question;
  const seconds = raw?.number_seconds;
  return typeof seconds === "number" && Number.isFinite(seconds)
    ? Math.max(0, seconds) * 1000
    : 0;
};

export class SurveySession {
  private phase: SurveySessionPhase = "idle";
  private attempt: SurveyAttempt | null = null;
  private pending: PendingCommand | null = null;
  private groupSubmission: GroupSubmission | null = null;
  private error: Error | null = null;
  private nextAttemptId = 0;
  private nextStoredSnapshotId = 0;
  private transcript: readonly SurveyTranscriptEntry[] = [];
  private storedValues: readonly StoredValueEntry[] = [];
  private hiddenStoredValue: StoredValueEntry | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly dialSamples = new Set<AbortController>();
  private dialSampleTail: Promise<void> = Promise.resolve();
  private dialSampleFailure: Error | null = null;
  private finalizingVideoAttemptId: number | null = null;
  private commentDraftQuestionReference: QuestionReference | null | undefined;
  private videoCompletionPending = false;
  private readonly comments = new Set<AbortController>();
  private commentSubmission: CommentSubmission | null = null;
  private readonly listeners = new Set<() => void>();
  private snapshot: SurveySessionSnapshot = {
    phase: "idle",
    attempt: null,
    pending: null,
    canSubmit: false,
    canUndo: false,
    transcript: [],
    storedValues: [],
    error: null,
  };

  constructor(
    private readonly transport: SurveyTransport,
    private readonly retryPolicy: SurveyRetryPolicy = DEFAULT_SURVEY_RETRY_POLICY,
    private readonly supportsQuestion: (question: Question) => boolean = () =>
      true,
    private readonly copy: SurveyCopy = defaultThemeV2.copy,
  ) {}

  getSnapshot = (): SurveySessionSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  start(): boolean {
    if (this.phase !== "idle") return false;
    this.send({ kind: "load" }, "loading");
    return true;
  }

  canSubmit(attempt: SurveyAttempt): boolean {
    const raw = isQuestionGroupContainerRaw(attempt.question.question)
      ? activeQuestionGroupChild(attempt.question.question)?.question
      : attempt.question.question;
    return this.canAnswer(attempt) && raw?.auto_response !== "ok";
  }

  private canAnswer(attempt: SurveyAttempt): boolean {
    return (
      this.isActiveAndReady(attempt) &&
      attempt.question.question.type !== "complete" &&
      attempt.question.question.type !== "webview_modal"
    );
  }

  canUndo(attempt: SurveyAttempt): boolean {
    return this.isActiveAndReady(attempt) && attempt.question.undoable === true;
  }

  submit(
    attempt: SurveyAttempt,
    answer: SurveyAnswer,
    projection: TranscriptProjection,
  ): boolean {
    this.groupSubmission = null;
    return this.submitPrepared(attempt, { answer, projection });
  }

  submitGroup(
    attempt: SurveyAttempt,
    groupId: string,
    resolve: (question: Question) => PreparedSurveyAnswer | null,
  ): boolean {
    if (
      !this.canSubmit(attempt) ||
      !isQuestionGroupContainerRaw(attempt.question.question)
    ) {
      return false;
    }
    const activeChildId = attempt.question.question.active_child_id;
    const prepared = resolve(attempt.question);
    if (!prepared) return false;
    this.groupSubmission = {
      groupId,
      resolve,
      lastChildId: activeChildId,
    };
    return this.submitPrepared(attempt, prepared);
  }

  submitDeferredGroupChild(attempt: SurveyAttempt): boolean {
    return this.continueGroupSubmission(attempt, true);
  }

  private submitPrepared(
    attempt: SurveyAttempt,
    { answer, projection }: PreparedSurveyAnswer,
  ): boolean {
    if (!this.canSubmit(attempt)) return false;
    this.clearTimer();
    this.appendTranscript({
      kind: "answer",
      attemptId: attempt.id,
      projection,
    });
    this.send({ kind: "answer", answer }, "submitting");
    return true;
  }

  submitDefault(attempt: SurveyAttempt): boolean {
    if (!this.canSubmit(attempt)) return false;
    this.groupSubmission = null;
    this.clearTimer();
    this.appendTranscript({
      kind: "action",
      attemptId: attempt.id,
      action: "default",
    });
    this.send({ kind: "default" }, "submitting");
    return true;
  }

  submitAutoResponse(attempt: SurveyAttempt): boolean {
    const raw = isQuestionGroupContainerRaw(attempt.question.question)
      ? activeQuestionGroupChild(attempt.question.question)?.question
      : attempt.question.question;
    if (!this.canAnswer(attempt) || !raw?.auto_response) {
      return false;
    }
    this.clearTimer();
    this.appendTranscript({
      kind: "action",
      attemptId: attempt.id,
      action: "auto-response",
    });
    this.send(
      {
        kind: "answer",
        answer: { type: "auto_response", value: AUTO_RESPONSE_MSG_CONTENT },
      },
      "submitting",
    );
    return true;
  }

  sampleDial(
    attempt: SurveyAttempt,
    value: number,
    offsetSeconds: number,
    postData: string,
  ): boolean {
    if (
      this.phase !== "active" ||
      this.attempt !== attempt ||
      attempt.question.question.type !== "video" ||
      this.finalizingVideoAttemptId === attempt.id ||
      !Number.isInteger(value) ||
      !Number.isInteger(offsetSeconds) ||
      offsetSeconds < 0 ||
      !postData
    )
      return false;
    if (!this.transport.storeDialSample) {
      this.error = new Error(this.copy.errors.dialUnavailable);
      this.publish();
      return false;
    }
    const controller = new AbortController();
    this.dialSamples.add(controller);
    this.dialSampleTail = this.dialSampleTail.then(async () => {
      try {
        if (
          this.phase === "disposed" ||
          controller.signal.aborted ||
          this.dialSampleFailure
        )
          return;
        await this.transport.storeDialSample!(
          { value, offsetSeconds, postData },
          controller.signal,
        );
      } catch (error: unknown) {
        if (this.phase === "disposed" || controller.signal.aborted) return;
        this.dialSampleFailure =
          error instanceof Error ? error : new Error(String(error));
        this.error = this.dialSampleFailure;
        this.publish();
      } finally {
        this.dialSamples.delete(controller);
      }
    });
    return true;
  }

  async completeVideo(
    attempt: SurveyAttempt,
    value: number | "",
    offsetSeconds: number,
    postData: string,
  ): Promise<boolean> {
    if (
      this.phase !== "active" ||
      this.attempt !== attempt ||
      attempt.question.question.type !== "video" ||
      this.finalizingVideoAttemptId !== null ||
      (value !== "" && !Number.isInteger(value)) ||
      !Number.isFinite(offsetSeconds) ||
      offsetSeconds < 0 ||
      !postData
    )
      return false;
    if (!this.transport.storeDialSample) {
      this.error = new Error(this.copy.errors.dialUnavailable);
      this.publish();
      return false;
    }

    this.finalizingVideoAttemptId = attempt.id;
    this.videoCompletionPending = true;
    this.error = null;
    this.publish();
    await this.dialSampleTail;
    if (this.phase !== "active" || this.attempt !== attempt) return false;
    if (this.dialSampleFailure) {
      this.resetVideoCompletionAfterFailure();
      return false;
    }

    const controller = new AbortController();
    this.dialSamples.add(controller);
    try {
      await this.transport.storeDialSample(
        { value, offsetSeconds, postData, last: true },
        controller.signal,
      );
      const completed = this.phase === "active" && this.attempt === attempt;
      if (completed) {
        this.videoCompletionPending = false;
        this.publish();
      }
      return completed;
    } catch (error: unknown) {
      if (!controller.signal.aborted) {
        this.error = error instanceof Error ? error : new Error(String(error));
        this.resetVideoCompletionAfterFailure();
      }
      return false;
    } finally {
      this.dialSamples.delete(controller);
    }
  }

  beginCommentDraft(): void {
    if (this.commentDraftQuestionReference === undefined) {
      this.commentDraftQuestionReference =
        this.attempt?.question.question_reference ?? null;
    }
  }

  dismissCommentDraft(comment: string): void {
    if (!comment.trim()) {
      this.commentSubmission = null;
      this.commentDraftQuestionReference = undefined;
    }
  }

  async submitComment(comment: string): Promise<void> {
    if (this.phase === "disposed")
      throw new Error(this.copy.errors.sessionClosed);
    if (!this.transport.submitComment) {
      throw new Error(this.copy.errors.commentUnavailable);
    }
    this.beginCommentDraft();
    if (this.commentSubmission?.body !== comment) {
      this.commentSubmission = {
        body: comment,
        sourceId: crypto.randomUUID(),
        questionReference: this.commentDraftQuestionReference ?? null,
      };
    }
    const submission = this.commentSubmission;
    const controller = new AbortController();
    this.comments.add(controller);
    try {
      await this.transport.submitComment(
        comment,
        submission.sourceId,
        submission.questionReference,
        controller.signal,
      );
      if (this.commentSubmission === submission) {
        this.commentSubmission = null;
        this.commentDraftQuestionReference = undefined;
      }
    } finally {
      this.comments.delete(controller);
    }
  }

  undoPrevious(attempt: SurveyAttempt): boolean {
    if (!this.canUndo(attempt)) return false;
    this.groupSubmission = null;
    this.clearTimer();
    this.appendTranscript({
      kind: "action",
      attemptId: attempt.id,
      action: "undo",
    });
    this.send({ kind: "undo", responseIndex: -1 }, "submitting");
    return true;
  }

  deleteStoredValue(attempt: SurveyAttempt, entry: StoredValueEntry): boolean {
    if (!this.canUndo(attempt) || !this.storedValues.includes(entry)) {
      return false;
    }
    this.clearTimer();
    this.hiddenStoredValue = entry;
    this.appendTranscript({
      kind: "action",
      attemptId: attempt.id,
      action: "delete-stored-value",
      target: entry.question,
    });
    this.send(
      { kind: "undo", responseIndex: entry.responseIndex },
      "submitting",
      entry,
    );
    return true;
  }

  dispose(): void {
    if (this.phase === "disposed") return;
    this.clearTimer();
    this.pending?.controller.abort();
    this.dialSamples.forEach((controller) => controller.abort());
    this.dialSamples.clear();
    this.dialSampleFailure = null;
    this.finalizingVideoAttemptId = null;
    this.videoCompletionPending = false;
    this.comments.forEach((controller) => controller.abort());
    this.comments.clear();
    this.pending = null;
    this.groupSubmission = null;
    this.attempt = null;
    this.transcript = [];
    this.storedValues = [];
    this.hiddenStoredValue = null;
    this.error = null;
    this.phase = "disposed";
    this.publish();
    this.listeners.clear();
  }

  private isActiveAndReady(attempt: SurveyAttempt): boolean {
    return (
      this.phase === "active" &&
      this.attempt === attempt &&
      !this.videoCompletionPending &&
      Date.now() >= attempt.availableAtMs
    );
  }

  private resetVideoCompletionAfterFailure(): void {
    this.finalizingVideoAttemptId = null;
    this.videoCompletionPending = false;
    this.dialSampleFailure = null;
    this.dialSampleTail = Promise.resolve();
    this.publish();
  }

  private appendTranscript(entry: SurveyTranscriptEntry): void {
    this.transcript = [...this.transcript, entry];
  }

  private send(
    command: SurveyCommand,
    phase: "loading" | "submitting",
    deletedValue?: StoredValueEntry,
  ) {
    const sequenceId = this.attempt?.question.sequence_id;
    const submittedCommand =
      (command.kind === "answer" || command.kind === "default") &&
      sequenceId !== undefined
        ? { ...command, sequenceId }
        : command;
    const pending: PendingCommand = {
      command: submittedCommand,
      controller: new AbortController(),
      ...(deletedValue ? { deletedValue } : {}),
    };
    this.pending = pending;
    this.phase = phase;
    this.error = null;
    this.publish();

    if (this.pending !== pending) return;

    void requestWithRecovery(
      this.transport,
      submittedCommand,
      pending.controller.signal,
      () => {
        if (this.pending !== pending || this.phase === "disposed") return;
        this.phase = "recovering";
        this.publish();
      },
      this.retryPolicy,
      this.copy.errors,
    ).then(
      (question) => this.accept(pending, question),
      (error: unknown) => this.fail(pending, error),
    );
  }

  private accept(pending: PendingCommand, question: Question | null): void {
    if (this.pending !== pending || this.phase === "disposed") return;
    this.pending = null;
    this.clearTimer();
    this.updateStoredValues(question, pending.deletedValue);

    if (!question) {
      this.groupSubmission = null;
      this.attempt = null;
      this.hiddenStoredValue = null;
      this.phase = "complete";
      this.publish();
      return;
    }

    if (!this.supportsQuestion(question)) {
      this.groupSubmission = null;
      this.attempt = null;
      this.hiddenStoredValue = null;
      this.error = new Error(
        formatCopy(this.copy.errors.unsupportedQuestion, {
          type: question.question.type,
        }),
      );
      this.phase = "error";
      this.publish();
      return;
    }

    const startedAtMs = Date.now();
    this.nextAttemptId += 1;
    const attempt: SurveyAttempt = {
      id: this.nextAttemptId,
      question,
      startedAtMs,
      availableAtMs: startedAtMs + questionDelayMs(question),
    };
    this.dialSampleTail = Promise.resolve();
    this.dialSampleFailure = null;
    this.finalizingVideoAttemptId = null;
    this.videoCompletionPending = false;
    this.attempt = attempt;
    this.hiddenStoredValue = null;
    this.appendTranscript({
      kind: "question",
      attemptId: attempt.id,
      question,
    });
    this.phase = "active";
    this.publish();
    if (this.phase === "active" && this.attempt === attempt) {
      this.armTimer(attempt);
    }
    this.continueGroupSubmission(attempt);
  }

  private continueGroupSubmission(
    attempt: SurveyAttempt,
    acknowledgedTimedImage = false,
  ): boolean {
    const submission = this.groupSubmission;
    const raw = attempt.question.question;
    if (!submission) return false;
    if (
      !isQuestionGroupContainerRaw(raw) ||
      raw.group_id !== submission.groupId
    ) {
      this.groupSubmission = null;
      return false;
    }
    const activeChild = activeQuestionGroupChild(raw);
    if (
      !activeChild ||
      activeChild.question.error_message ||
      activeChild.id === submission.lastChildId
    ) {
      this.groupSubmission = null;
      return false;
    }
    if (activeChild.question.auto_response === "ok") return false;
    if (
      !acknowledgedTimedImage &&
      activeChild.question.type === "image" &&
      Number(activeChild.question.number_seconds) > 0
    ) {
      return false;
    }
    const prepared = submission.resolve(attempt.question);
    if (!prepared) {
      this.groupSubmission = null;
      return false;
    }
    if (this.submitPrepared(attempt, prepared)) {
      submission.lastChildId = activeChild.id;
      return true;
    }
    return false;
  }

  private updateStoredValues(
    question: Question | null,
    deletedValue?: StoredValueEntry,
  ): void {
    if (!question && !deletedValue) return;

    const entries =
      question?.stored_values !== undefined
        ? Object.entries(question.stored_values).map(([name, value]) => ({
            question: name,
            response: String(value),
          }))
        : this.storedValues
            .filter((entry) => entry !== deletedValue)
            .map(({ question: name, response }) => ({
              question: name,
              response,
            }));

    this.nextStoredSnapshotId += 1;
    this.storedValues = entries.map((entry, index) => ({
      ...entry,
      responseIndex: index + 1,
      snapshotId: this.nextStoredSnapshotId,
    }));
  }

  private fail(pending: PendingCommand, error: unknown): void {
    if (this.pending !== pending || this.phase === "disposed") return;
    this.pending = null;
    this.groupSubmission = null;
    this.hiddenStoredValue = null;
    this.error = error instanceof Error ? error : new Error(String(error));
    this.phase = "error";
    this.publish();
  }

  private armTimer(attempt: SurveyAttempt): void {
    const raw = isQuestionGroupContainerRaw(attempt.question.question)
      ? activeQuestionGroupChild(attempt.question.question)?.question
      : attempt.question.question;
    const shouldSubmitAutomatically =
      raw?.auto_response === "ok" &&
      attempt.question.question.type !== "complete" &&
      attempt.question.question.type !== "webview_modal";
    const remainingMs = Math.max(0, attempt.availableAtMs - Date.now());
    if (remainingMs === 0 && !shouldSubmitAutomatically) return;

    this.timer = setTimeout(
      () => {
        this.timer = null;
        if (this.phase !== "active" || this.attempt !== attempt) return;
        if (Date.now() < attempt.availableAtMs) {
          this.armTimer(attempt);
          return;
        }
        this.publish();
        this.continueGroupSubmission(attempt);
        if (shouldSubmitAutomatically) this.submitAutoResponse(attempt);
      },
      Math.min(remainingMs, MAX_TIMEOUT_MS),
    );
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private publish(): void {
    this.snapshot = {
      phase: this.phase,
      attempt: this.attempt,
      pending: this.pending?.command.kind ?? null,
      canSubmit: this.attempt !== null && this.canSubmit(this.attempt),
      canUndo: this.attempt !== null && this.canUndo(this.attempt),
      transcript: this.transcript,
      storedValues: this.hiddenStoredValue
        ? this.storedValues.filter((entry) => entry !== this.hiddenStoredValue)
        : this.storedValues,
      error: this.error,
    };
    this.listeners.forEach((listener) => listener());
  }
}
